import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { 
  ArrowLeft, Download, Copy, RefreshCw, Calendar,
  DollarSign, Eye, CheckCircle, XCircle, TrendingUp, FileText,
  Globe, Shield, Clock, AlertTriangle, Users, MousePointer, Target
} from 'lucide-react';
import { getCurrencySymbol, formatCurrency } from './utils/currency';

// const API_URL = 'http://localhost:8080';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export default function CampaignDetail({ campaign, onBack }) {
  const [stats, setStats] = useState(null);
  const [conversionStats, setConversionStats] = useState(null);
  const [recentConversions, setRecentConversions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateRange, setDateRange] = useState(30);
  const [hoveredDay, setHoveredDay] = useState(null);

  const currency = campaign.currency || 'USD';

  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
    
    setToDate(today.toISOString().split('T')[0]);
    setFromDate(thirtyDaysAgo.toISOString().split('T')[0]);
    
    fetchCampaignStats();
  }, [campaign.id]);

  const fetchCampaignStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const userType = localStorage.getItem('userType');
      
      let url;
      if (userType === 'publisher') {
        url = `${API_URL}/api/publishers/campaigns/${campaign.id}/stats`;
      } else {
        url = `${API_URL}/api/analytics/campaigns/${campaign.id}/stats`;
      }
      
      if (fromDate && toDate) {
        url += `?from=${fromDate}&to=${toDate}`;
      } else {
        url += `?period=${dateRange}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }

      // Fetch conversion stats
      try {
        const convStatsRes = await fetch(
          `${API_URL}/api/campaigns/${campaign.id}/conversions/stats`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (convStatsRes.ok) {
          const convData = await convStatsRes.json();
          setConversionStats(convData);
        }
      } catch (err) {
        console.error('Failed to fetch conversion stats:', err);
      }

      // Fetch recent conversions
      try {
        const conversionsRes = await fetch(
          `${API_URL}/api/campaigns/${campaign.id}/conversions?limit=50`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (conversionsRes.ok) {
          const convList = await conversionsRes.json();
          setRecentConversions(convList);
        }
      } catch (err) {
        console.error('Failed to fetch conversions:', err);
      }

    } catch (error) {
      console.error('Failed to fetch campaign stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadStats = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const userType = localStorage.getItem('userType');
      
      let url;
      if (userType === 'publisher') {
        url = `${API_URL}/api/publishers/campaigns/${campaign.id}/download-stats`;
      } else {
        url = `${API_URL}/api/analytics/campaigns/${campaign.id}/download-stats`;
      }
      
      if (fromDate && toDate) {
        url += `?from=${fromDate}&to=${toDate}`;
      } else {
        url += `?period=${dateRange}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `campaign_${campaign.id}_stats.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error('Failed to download stats:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const totals = stats?.daily_stats?.reduce((acc, day) => ({
    total_clicks: acc.total_clicks + (day.total_clicks || 0),
    valid_clicks: acc.valid_clicks + (day.valid_clicks || 0),
    invalid_clicks: acc.invalid_clicks + (day.invalid_clicks || 0),
    foreign_clicks: acc.foreign_clicks + (day.foreign_clicks || 0),
    bot_clicks: acc.bot_clicks + (day.bot_clicks || 0),
    expired_clicks: acc.expired_clicks + (day.expired_clicks || 0),
    duplicate_clicks: acc.duplicate_clicks + (day.duplicate_clicks || 0),
    revenue: acc.revenue + (day.revenue || 0)
  }), { 
    total_clicks: 0, 
    valid_clicks: 0, 
    invalid_clicks: 0,
    foreign_clicks: 0,
    bot_clicks: 0,
    expired_clicks: 0,
    duplicate_clicks: 0,
    revenue: 0 
  }) || {};

  const avgCPC = totals.valid_clicks > 0 ? (totals.revenue / totals.valid_clicks) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h2 className="text-2xl font-bold">{campaign.name}</h2>
          <Badge variant="outline">{campaign.country_code}</Badge>
          <Badge variant="outline">{currency}</Badge>
          <Badge variant={campaign.status === 'active' ? 'default' : 'secondary'}>
            {campaign.status}
          </Badge>
          {conversionStats && conversionStats.total_conversions > 0 && (
            <Badge variant="outline" className="text-blue-600 border-blue-300">
              <Target className="h-3 w-3 mr-1" />
              {conversionStats.conversion_rate}% CR
            </Badge>
          )}
        </div>
      </div>

      {/* Date Filter & Feed URL Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Date Range</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="flex-1"
              />
              <span>to</span>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="flex-1"
              />
              <Button onClick={fetchCampaignStats} size="sm">
                <Calendar className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {(campaign.http_url || campaign.output_url) && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Feed URL</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                <Input
                  value={campaign.http_url || campaign.output_url || ''}
                  readOnly
                  className="flex-1 text-sm"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigator.clipboard.writeText(campaign.http_url || campaign.output_url)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline"
                  size="sm" 
                  onClick={handleDownloadStats}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Performance Overview Cards - NOW WITH CONVERSIONS */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <MetricCard
          title="Total Revenue"
          value={formatCurrency(totals.revenue, currency)}
          icon={<span className="text-xl font-bold">{getCurrencySymbol(currency)}</span>}
          trend={`${formatCurrency(avgCPC, currency)} avg CPC`}
          color="green"
        />
        <MetricCard
          title="Valid Clicks"
          value={totals.valid_clicks}
          icon={<CheckCircle className="h-5 w-5" />}
          trend={`${totals.total_clicks > 0 ? ((totals.valid_clicks / totals.total_clicks) * 100).toFixed(1) : 0}% of total`}
          color="blue"
        />
        <MetricCard
          title="Invalid Clicks"
          value={totals.invalid_clicks}
          icon={<XCircle className="h-5 w-5" />}
          trend={`${totals.total_clicks > 0 ? ((totals.invalid_clicks / totals.total_clicks) * 100).toFixed(1) : 0}% of total`}
          color="red"
        />
        <MetricCard
          title="Total Clicks"
          value={totals.total_clicks}
          icon={<MousePointer className="h-5 w-5" />}
          trend={`${campaign.total_urls || 0} jobs`}
          color="purple"
        />
        {/* NEW: Conversions Card */}
        <MetricCard
          title="Conversions"
          value={conversionStats?.valid_conversions || 0}
          icon={<Target className="h-5 w-5" />}
          trend={`${conversionStats?.conversion_rate || 0}% CR`}
          color="orange"
        />
      </div>

      {/* Invalid Click Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Click Quality Analysis</CardTitle>
          <CardDescription>Breakdown of invalid click reasons</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InvalidClickCard
              type="Foreign"
              count={totals.foreign_clicks}
              total={totals.invalid_clicks}
              icon={<Globe className="h-4 w-4" />}
              color="orange"
            />
            <InvalidClickCard
              type="Bot"
              count={totals.bot_clicks}
              total={totals.invalid_clicks}
              icon={<Shield className="h-4 w-4" />}
              color="red"
            />
            <InvalidClickCard
              type="Expired"
              count={totals.expired_clicks}
              total={totals.invalid_clicks}
              icon={<Clock className="h-4 w-4" />}
              color="yellow"
            />
            <InvalidClickCard
              type="Duplicate"
              count={totals.duplicate_clicks}
              total={totals.invalid_clicks}
              icon={<Users className="h-4 w-4" />}
              color="purple"
            />
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Revenue & Click Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Trend</CardTitle>
          <CardDescription>Revenue and click patterns over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <InteractiveChart 
              data={stats?.daily_stats || []} 
              hoveredDay={hoveredDay}
              setHoveredDay={setHoveredDay}
              currency={currency}
            />
          </div>
          {hoveredDay && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-900">
                {new Date(hoveredDay.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2 text-sm">
                <div>
                  <span className="text-gray-500">Revenue:</span>
                  <span className="ml-2 font-medium text-green-600">
                    {formatCurrency(hoveredDay.revenue, currency)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Valid:</span>
                  <span className="ml-2 font-medium">{hoveredDay.valid_clicks}</span>
                </div>
                <div>
                  <span className="text-gray-500">Invalid:</span>
                  <span className="ml-2 font-medium">{hoveredDay.invalid_clicks}</span>
                </div>
                <div>
                  <span className="text-gray-500">CTR:</span>
                  <span className="ml-2 font-medium">{hoveredDay.click_rate.toFixed(1)}%</span>
                </div>
              </div>
              {hoveredDay.invalid_clicks > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-2 text-xs">
                  {hoveredDay.foreign_clicks > 0 && (
                    <Badge variant="outline">Foreign: {hoveredDay.foreign_clicks}</Badge>
                  )}
                  {hoveredDay.bot_clicks > 0 && (
                    <Badge variant="outline">Bot: {hoveredDay.bot_clicks}</Badge>
                  )}
                  {hoveredDay.expired_clicks > 0 && (
                    <Badge variant="outline">Expired: {hoveredDay.expired_clicks}</Badge>
                  )}
                  {hoveredDay.duplicate_clicks > 0 && (
                    <Badge variant="outline">Duplicate: {hoveredDay.duplicate_clicks}</Badge>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Daily Statistics Table - WITH CR% COLUMN */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Daily Statistics</CardTitle>
              <CardDescription>Detailed breakdown by day</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleDownloadStats}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium">Date</th>
                  <th className="text-center p-3 font-medium">Total</th>
                  <th className="text-center p-3 font-medium">Valid</th>
                  <th className="text-center p-3 font-medium">Foreign</th>
                  <th className="text-center p-3 font-medium">Bot</th>
                  <th className="text-center p-3 font-medium">Expired</th>
                  <th className="text-center p-3 font-medium">Duplicate</th>
                  <th className="text-center p-3 font-medium">CTR</th>
                  <th className="text-center p-3 font-medium">CR</th>
                  <th className="text-right p-3 font-medium">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {stats?.daily_stats?.map((day) => (
                  <tr key={day.date} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="p-3 font-medium">
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </td>
                    <td className="text-center p-3">{day.total_clicks}</td>
                    <td className="text-center p-3">
                      <span className="text-green-600 font-medium">{day.valid_clicks}</span>
                    </td>
                    <td className="text-center p-3 text-orange-600">
                      {day.foreign_clicks || '-'}
                    </td>
                    <td className="text-center p-3 text-red-600">
                      {day.bot_clicks || '-'}
                    </td>
                    <td className="text-center p-3 text-yellow-600">
                      {day.expired_clicks || '-'}
                    </td>
                    <td className="text-center p-3 text-purple-600">
                      {day.duplicate_clicks || '-'}
                    </td>
                    <td className="text-center p-3">
                      <Badge variant={day.click_rate >= 70 ? 'default' : day.click_rate >= 40 ? 'secondary' : 'destructive'}>
                        {day.click_rate.toFixed(0)}%
                      </Badge>
                    </td>
                    <td className="text-center p-3">
                      <span className="text-blue-600 font-medium">0%</span>
                    </td>
                    <td className="text-right p-3 font-medium text-green-600">
                      {formatCurrency(day.revenue, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-medium">
                  <td className="p-3">Total</td>
                  <td className="text-center p-3">{totals.total_clicks}</td>
                  <td className="text-center p-3 text-green-600">{totals.valid_clicks}</td>
                  <td className="text-center p-3 text-orange-600">{totals.foreign_clicks || '-'}</td>
                  <td className="text-center p-3 text-red-600">{totals.bot_clicks || '-'}</td>
                  <td className="text-center p-3 text-yellow-600">{totals.expired_clicks || '-'}</td>
                  <td className="text-center p-3 text-purple-600">{totals.duplicate_clicks || '-'}</td>
                  <td className="text-center p-3">
                    {totals.total_clicks > 0 ? 
                      `${((totals.valid_clicks / totals.total_clicks) * 100).toFixed(0)}%` : '-'}
                  </td>
                  <td className="text-center p-3 text-blue-600 font-medium">
                    {conversionStats?.conversion_rate || 0}%
                  </td>
                  <td className="text-right p-3 text-green-600">
                    {formatCurrency(totals.revenue, currency)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* NEW: Recent Conversions Table */}
      {recentConversions.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Recent Conversions</CardTitle>
                <CardDescription>
                  Latest {recentConversions.length} tracked conversions (applications)
                </CardDescription>
              </div>
              <Badge variant="outline" className="text-blue-600 border-blue-300">
                {conversionStats?.total_conversions || 0} Total
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-medium">Date/Time</th>
                    <th className="text-left p-3 font-medium">Type</th>
                    <th className="text-center p-3 font-medium">Value</th>
                    <th className="text-left p-3 font-medium">Job Reference</th>
                    <th className="text-left p-3 font-medium">IP Address</th>
                    <th className="text-center p-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentConversions.map((conv) => (
                    <tr key={conv.id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="p-3">
                        {new Date(conv.fired_at).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className="capitalize">
                          {conv.conversion_type || 'completed'}
                        </Badge>
                      </td>
                      <td className="text-center p-3 font-medium">
                        {conv.conversion_value > 0 
                          ? formatCurrency(conv.conversion_value, currency)
                          : '-'
                        }
                      </td>
                      <td className="p-3 text-gray-600 font-mono text-xs">
                        {conv.job_reference || '-'}
                      </td>
                      <td className="p-3 text-gray-600">
                        {conv.user_ip || '-'}
                      </td>
                      <td className="text-center p-3">
                        {conv.is_valid ? (
                          <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Valid
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            <XCircle className="h-3 w-3 mr-1" />
                            Invalid
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Metric Card Component
function MetricCard({ title, value, icon, trend, color }) {
  const colorClasses = {
    green: 'bg-green-50 text-green-600 border-green-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200'
  };

  return (
    <Card className={`border ${colorClasses[color].split(' ')[2]}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            <p className="text-xs text-gray-500 mt-1">{trend}</p>
          </div>
          <div className={`p-2 rounded-lg ${colorClasses[color].split(' ').slice(0, 2).join(' ')}`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Invalid Click Card Component
function InvalidClickCard({ type, count, total, icon, color }) {
  const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
  const colorClasses = {
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600'
  };

  return (
    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
      <div className={`p-2 rounded ${colorClasses[color]}`}>
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium">{type}</p>
        <p className="text-lg font-bold">{count}</p>
        <p className="text-xs text-gray-500">{percentage}% of invalid</p>
      </div>
    </div>
  );
}

// Interactive Chart Component
function InteractiveChart({ data, hoveredDay, setHoveredDay, currency }) {
  if (!data || data.length === 0) return <div className="flex items-center justify-center h-full text-gray-500">No data available</div>;

  const maxRevenue = Math.max(...data.map(d => d.revenue || 0));
  const maxClicks = Math.max(...data.map(d => d.total_clicks || 0));
  const currencySymbol = getCurrencySymbol(currency);
  
  return (
    <svg className="w-full h-full" viewBox="0 0 800 320">
      {/* Grid */}
      {[0, 1, 2, 3, 4].map(i => (
        <line
          key={i}
          x1="60"
          y1={60 + i * 50}
          x2="740"
          y2={60 + i * 50}
          stroke="#e5e7eb"
          strokeWidth="1"
        />
      ))}
      
      {/* Y-axis labels */}
      <text x="40" y="65" fontSize="10" fill="#6b7280" textAnchor="end">
        {currencySymbol}{maxRevenue.toFixed(0)}
      </text>
      <text x="40" y="265" fontSize="10" fill="#6b7280" textAnchor="end">
        {currencySymbol}0
      </text>
      
      {/* Data points and lines */}
      {data.map((day, i) => {
        const x = 60 + (i * 680 / Math.max(data.length - 1, 1));
        const revenueY = 260 - ((day.revenue || 0) / Math.max(maxRevenue, 1) * 200);
        const clicksY = 260 - ((day.total_clicks || 0) / Math.max(maxClicks, 1) * 200);
        
        return (
          <g key={day.date}>
            {/* Hover area */}
            <rect
              x={x - 20}
              y={40}
              width={40}
              height={240}
              fill="transparent"
              onMouseEnter={() => setHoveredDay(day)}
              onMouseLeave={() => setHoveredDay(null)}
              style={{ cursor: 'pointer' }}
            />
            
            {/* Revenue line */}
            {i > 0 && (
              <line
                x1={60 + ((i - 1) * 680 / Math.max(data.length - 1, 1))}
                y1={260 - ((data[i - 1].revenue || 0) / Math.max(maxRevenue, 1) * 200)}
                x2={x}
                y2={revenueY}
                stroke="#10b981"
                strokeWidth="2"
              />
            )}
            
            {/* Revenue dot */}
            <circle
              cx={x}
              cy={revenueY}
              r={hoveredDay?.date === day.date ? 6 : 4}
              fill="#10b981"
              stroke="white"
              strokeWidth="2"
            />
            
            {/* Invalid clicks stacked bar */}
            {day.invalid_clicks > 0 && (
              <g>
                {day.foreign_clicks > 0 && (
                  <rect
                    x={x - 8}
                    y={260 - (day.foreign_clicks / Math.max(maxClicks, 1) * 200)}
                    width={16}
                    height={(day.foreign_clicks / Math.max(maxClicks, 1) * 200)}
                    fill="#fb923c"
                    opacity="0.7"
                  />
                )}
                {day.bot_clicks > 0 && (
                  <rect
                    x={x - 8}
                    y={260 - ((day.foreign_clicks + day.bot_clicks) / Math.max(maxClicks, 1) * 200)}
                    width={16}
                    height={(day.bot_clicks / Math.max(maxClicks, 1) * 200)}
                    fill="#ef4444"
                    opacity="0.7"
                  />
                )}
                {day.expired_clicks > 0 && (
                  <rect
                    x={x - 8}
                    y={260 - ((day.foreign_clicks + day.bot_clicks + day.expired_clicks) / Math.max(maxClicks, 1) * 200)}
                    width={16}
                    height={(day.expired_clicks / Math.max(maxClicks, 1) * 200)}
                    fill="#eab308"
                    opacity="0.7"
                  />
                )}
              </g>
            )}
          </g>
        );
      })}
      
      {/* Legend */}
      <g transform="translate(60, 290)">
        <circle cx="0" cy="0" r="4" fill="#10b981" />
        <text x="10" y="4" fontSize="11" fill="#6b7280">Revenue</text>
        
        <rect x="80" y="-4" width="8" height="8" fill="#fb923c" opacity="0.7" />
        <text x="92" y="4" fontSize="11" fill="#6b7280">Foreign</text>
        
        <rect x="140" y="-4" width="8" height="8" fill="#ef4444" opacity="0.7" />
        <text x="152" y="4" fontSize="11" fill="#6b7280">Bot</text>
        
        <rect x="180" y="-4" width="8" height="8" fill="#eab308" opacity="0.7" />
        <text x="192" y="4" fontSize="11" fill="#6b7280">Expired</text>
      </g>
    </svg>
  );
}